<template>
<div
  id="plans"
  class="block md:grid gap-x-lg grid-cols-pageWide mb-4 pt-sm">
  <h3 id="change-plan-title" class="mb-none col-span-full">{{ $t('companySettings.billing.changePlanSection.title') }}</h3>
  <hr class="my-sm col-span-full" />
  <div class="col-span-1 mb-base">
    <p id="change-plan-info1">{{ $t('companySettings.billing.changePlanSection.info1') }}</p>
    <p id="change-plan-info2">{{ $t('companySettings.billing.changePlanSection.info2') }}</p>
    <i18n id="change-plan-info3" path="companySettings.billing.changePlanSection.info3" tag="p">
      <template v-slot:link>
        <a
          id="change-plan-letsTalk-link"
          target="_blank"
          href="https://www.signable.co.uk/contact"
          data-ec="billing_page"
          data-et="lets_talk_contact_link">
          {{ $t('companySettings.billing.changePlanSection.letsTalk') }}
        </a>
      </template>
    </i18n>
  </div>
  <div>
    <div v-if="privatePlans.length" id="change-plan-custom-plans">
      <b id="change-plan-custom-plans-title" class="col-span-1 block mb-base">{{ $t('companySettings.billing.changePlanSection.customPlans') }}</b>
      <BillingPlanCard
        v-for="(plan, index) in privatePlans"
        :id="`change-plan-custom-plans-${plan.id}`"
        :key="plan.id"
        :plan="plan"
        :currentPlan="currentPlan"
        v-on:openModal="openCustomPlanSwitchPreview(index)" />
    </div>
    <b class="col-span-1 block mb-base">{{ $t('companySettings.billing.changePlanSection.signablePlans') }}</b>
    <div class="flex items-center mb-base bg-offWhite p-xs rounded-9">
      <VCheckbox
        id="change-plan-annual-checkbox"
        class="ml-xs"
        type="checkbox"
        subtype="checkbox"
        v-model="intervalBool"
        :checked="intervalBool"
        data-ec="billing_page"
        data-et="plan_period_toggle"
      />
      <b id="change-plan-annual-checkbox-text" class="ml-base">{{ $t('companySettings.billing.changePlanSection.showAnnual') }}</b>
    </div>
    <div id="change-plan-signable-plans">
      <BillingPlanCard
          v-for="(plan, index) in plansByInterval"
          :id="`change-plan-signable-plans-${plan.id}`"
          :key="plan.id"
          :plan="plan"
          :currentPlan="currentPlan"
          v-on:openModal="openPlanSwitchPreview(index)" />
      <BillingPlanCard
          :paygPrice="paygPrice"
          :currentPlan="currentPlan"
          :plan="paygPlans[0]"
          v-on:openModal="openSelectPlanModalById('pay-as-you-go')" />
    </div>
  </div>
  <VDynamicLoader
    viewURL="modals/payment/SelectPlanModal"
    :data="{
      currentPlan,
      selectedPlan,
      paymentMethodAvailable: !!paymentMethods.length
    }"
  />
  <SwitchPlanInvoicePreviewModal
    :currentPlan="currentPlan"
    :paymentMethodPending="!!paymentMethodPending"
    :paymentMethodAvailable="!!paymentMethods.length"
    :overagesEnabled="!!company.billing_overage"
    :selectedPlan="selectedPlan"
    :show="showPlanInvoicePreviewModal"
    @close="showPlanInvoicePreviewModal = false;"
    @plan-switched="$emit('plan-switched')"
  />
  </div>
</template>

<script>

import SwitchPlanInvoicePreviewModal from '@/views/modals/payment/PlanSwitchInvoicePreviewModal.vue';
import VCheckbox from '@/components/deprecated/atoms/Checkbox.vue';
import BillingPlanCard from '@/views/components/billing/PlanCard.vue';
import VDynamicLoader from '@/components/DynamicLoader.vue';

export default {
  name: 'BillingChangePlan',
  components: {
    VCheckbox,
    BillingPlanCard,
    SwitchPlanInvoicePreviewModal,
    VDynamicLoader,
  },
  props: {
    plansAvailable: Array,
    paygPrice: String,
    currentPlan: Object,
    paymentMethods: Array,
    company: Object,
  },
  data() {
    return {
      showPlanInvoicePreviewModal: false,
      intervalBool: false,
      selectedPlan: {},
    };
  },
  computed: {
    privatePlans() {
      if (this.plansAvailable) return this.plansAvailable.filter(plan => plan.type === 'Private');
      return false;
    },
    paygPlans() {
      if (this.plansAvailable) return this.plansAvailable.filter(plan => plan.type === 'PayAsYouGo');
      return false;
    },
    paymentMethodPending() {
      if (this.paymentMethods && this.paymentMethods.length) {
        const defaultMethod = this.paymentMethods.find(method => method.is_default === true);
        if (defaultMethod.status === 'pending') return true;
      }

      return false;
    },
    plansByInterval() { // Fetch the first 4 (largest by envelope allowance) plans for the selected billing interval
      if (this.plansAvailable) {
        const plans = this.plansAvailable.filter(plan => (plan.interval === this.interval) && (plan.type === 'Public'));
        plans.sort((a, b) => b.allowance - a.allowance);
        if (plans.length > 4) plans.length = 4;
        return plans;
      }

      return false;
    },
    interval() {
      if (this.intervalBool) return 'year';
      return 'month';
    },
  },
  mounted() {
    /**
     * A super-hacky workaround to scroll to this component. We could implement this
     * via vue-router's scrollBehaviour() method, but since we have a loading state
     * that queries the backend, this component isn't mounted until after vue-router
     * has performed the work, meaning that it doesn't scroll.
     *
     * This can be revisited when more frontend upgrade work has been done.
     *
     * We have a guard to only check for '#plans'
     */
    if (window.location.hash === '#plans') {
      const element = document.querySelector('#plans');
      element.scrollIntoView();
    }
  },
  methods: {
    openPlanSwitchPreview(index) {
      this.selectedPlan = this.plansByInterval[index];
      this.showPlanInvoicePreviewModal = true;
    },
    openCustomPlanSwitchPreview(index) {
      this.selectedPlan = this.privatePlans[index];
      this.showPlanInvoicePreviewModal = true;
    },
    openSelectPlanModalById(id) {
      this.selectedPlan = this.plansAvailable.find(plan => plan.id === id);
      if (this.selectedPlan && (this.selectedPlan.id === 'free-trial' || this.selectedPlan.id === 'pay-as-you-go')) {
        this.$bus.$emit('toggleModal', { name: 'select-plan-modal', active: true });
      } else if (this.selectedPlan) {
        this.showPlanInvoicePreviewModal = true;
      } else {
        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.fail.find', { name: 'plan' }),
          icon: 'times',
          type: 'error',
        }));
      }
    },
  },
};
</script>
